import type { TelemetryInterceptor } from '@smiledragon-corp/signoz-nextjs/axios';
import { AuthInterceptor } from './authInterceptor';
import { createClientTelemetryInterceptor } from './telemetryInterceptor';

/**
 * response FIFO **첫 번째** 자리에서 원본 AxiosError를 telemetry에 먼저 흘린다.
 *
 * 정본 배열에서 telemetry는 index 4(맨 뒤)에 있어야 한다 — libs-frontend interceptors의
 * 타입 튜플이 index 0/1/2를 Auth/Error/DuplicateRequest 전용으로 고정하기 때문이다.
 * 그런데 response는 FIFO라 맨 뒤 = 가장 늦게 실행이고, 그때는 ErrorInterceptor가
 * NetworkError/UnknownError로, 이 클래스가 AuthorizationError로 원본을 갈아치운 뒤라
 * `.config`가 없다 → `{url:'', traceId:undefined}` breadcrumb → acceptAPI가 건너뛰고
 * 과거 trace 채택(오귀속). 그래서 **실패 기록만** 이 자리로 끌어올린다.
 *
 * 짝: `httpClient.ts`의 tail 슬롯은 `responseInterceptorOnRejected`를 뺀다. 이 분업을
 * 되돌리면 오귀속이 재발하거나 breadcrumb이 중복된다.
 * libs-frontend가 preInterceptors를 지원하면 이 클래스는 삭제 대상이다.
 */
export class TelemetryFirstAuthInterceptor extends AuthInterceptor {
  private _telemetry?: TelemetryInterceptor | null; // undefined=미평가, null=비활성 확정

  override get responseInterceptorOnRejected(): (error: unknown) => Promise<never> {
    const delegate = super.responseInterceptorOnRejected;

    return ((error: unknown) => {
      if (this._telemetry === undefined) {
        this._telemetry = createClientTelemetryInterceptor() ?? null;
      }
      try {
        // SDK는 동기 기록 후 rejected promise를 돌려준다 — 삼키고 원래 체인으로 넘긴다.
        void Promise.resolve(this._telemetry?.responseInterceptorOnRejected?.(error)).catch(
          () => {},
        );
      } catch {
        // 계측 실패가 auth 흐름을 막아선 안 된다.
      }
      return delegate(error);
    }) as (error: unknown) => Promise<never>;
  }
}
